import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { z } from "zod";

import { useAuth } from "@/lib/auth/AuthProvider";
import { config } from "@/lib/config";
import { tokenStore } from "@/lib/auth/tokenStore";
import { networkError, parseErrorResponse, type ApiError } from "@/lib/api/errors";
import { secretFromProvisioningUri } from "@/lib/auth/totp";

interface Props {
  // Any one of the listed roles is sufficient. Empty array means
  // authenticated-only.
  anyOf?: string[];
  children: ReactNode;
  // Where to send the user when the role check fails. Defaults to the
  // console home (/overview).
  forbiddenRedirect?: string;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <div style={{ padding: 24 }}>Loading…</div>;
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}

export function RequireRole({ anyOf, children, forbiddenRedirect = "/overview" }: Props) {
  const { user, roles, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <div style={{ padding: 24 }}>Loading…</div>;
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (anyOf && anyOf.length > 0) {
    const hasMatch = anyOf.some((role) => roles.includes(role));
    if (!hasMatch) {
      return <Navigate to={forbiddenRedirect} replace />;
    }
  }
  return <>{children}</>;
}

export function hasAnyRole(roles: string[], required: string[]): boolean {
  if (required.length === 0) return true;
  return required.some((role) => roles.includes(role));
}

// ---------------------------------------------------------------------------
// Forced MFA enrollment
//
// A logged-in user whose session still carries mfaSetupRequired=true was issued
// tokens but has no TOTP secret yet. They are HARD-GATED here: the only thing
// they can do is enroll. There is intentionally NO skip / cancel / back control.
// This uses the AUTHENTICATED setup path (bearer token), distinct from the
// pre-login challenge-token enrollment in Login.tsx.
// ---------------------------------------------------------------------------

const enrollSetupSchema = z.object({ provisioning_uri: z.string(), qr_svg: z.string().optional() });
const enrollVerifySchema = z.object({ mfa_enabled: z.boolean() });

async function postAuthed<S extends z.ZodTypeAny>(
  path: string,
  body: Record<string, unknown> | undefined,
  schema: S,
): Promise<{ data: z.output<S> } | { error: ApiError }> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (tokenStore.accessToken) headers.Authorization = `Bearer ${tokenStore.accessToken}`;
  try {
    const res = await fetch(`${config.authBaseUrl}${path}`, {
      method: "POST",
      headers,
      credentials: "include",
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) return { error: await parseErrorResponse(res) };
    const json = (await res.json().catch(() => ({}))) as unknown;
    const parsed = schema.safeParse(json);
    if (!parsed.success) {
      return {
        error: {
          code: "unknown",
          message: "auth-service returned an unexpected response shape",
          status: res.status,
          detail: parsed.error.issues,
        },
      };
    }
    return { data: parsed.data };
  } catch (err) {
    return { error: networkError(err) };
  }
}

function MandatoryMfaEnrollment() {
  const { markMfaEnrolled } = useAuth();
  const [provisioningUri, setProvisioningUri] = useState<string | null>(null);
  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [loadingSetup, setLoadingSetup] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Kick off setup once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await postAuthed("/auth/mfa/setup", undefined, enrollSetupSchema);
      if (cancelled) return;
      setLoadingSetup(false);
      if ("error" in result) {
        // If MFA somehow already got enabled (409), treat the gate as satisfied.
        if (result.error.status === 409) {
          markMfaEnrolled();
          return;
        }
        setError(result.error.message);
        return;
      }
      setProvisioningUri(result.data.provisioning_uri);
      setQrSvg(result.data.qr_svg ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [markMfaEnrolled]);

  useEffect(() => {
    if (provisioningUri) inputRef.current?.focus();
  }, [provisioningUri]);

  async function onConfirm(e: React.FormEvent) {
    e.preventDefault();
    if (code.length < 6) return;
    setConfirming(true);
    setError(null);
    const result = await postAuthed("/auth/mfa/verify", { code: code.replace(/\s/g, "") }, enrollVerifySchema);
    setConfirming(false);
    if ("error" in result) {
      setError(result.error.message);
      return;
    }
    if (result.data.mfa_enabled) {
      markMfaEnrolled();
    } else {
      setError("Verification failed. Check the code and try again.");
    }
  }

  const secret = provisioningUri ? secretFromProvisioningUri(provisioningUri) : null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#050505] px-4">
      <div className="w-full max-w-md rounded-lg border border-zinc-900 bg-[#0d0d0d] p-8">
        <div className="mb-5 flex items-center gap-2 text-zinc-300">
          <Lock className="h-5 w-5" aria-hidden="true" />
          <h1 className="text-lg font-semibold text-zinc-100">Set up two-factor authentication</h1>
        </div>
        <p className="mb-6 text-xs text-zinc-500">
          Your account requires an authenticator app before you can continue. Scan the QR code, then enter the
          6-digit code to finish.
        </p>

        {loadingSetup ? (
          <div className="flex items-center gap-2 text-sm text-zinc-400">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Preparing enrollment…
          </div>
        ) : provisioningUri ? (
          <div className="space-y-5">
            <div className="rounded-lg border border-zinc-900 bg-[#0f0f0f] p-5">
              <h2 className="mb-3 text-sm font-medium text-zinc-200">1. Scan with your authenticator app</h2>
              {qrSvg ? (
                <div
                  className="inline-block rounded-lg bg-white p-3"
                  aria-label="TOTP QR code — scan with authenticator app"
                  dangerouslySetInnerHTML={{ __html: qrSvg }}
                />
              ) : (
                <div className="inline-block rounded-lg bg-white p-3" aria-label="TOTP QR code — scan with authenticator app">
                  <QRCodeSVG value={provisioningUri} size={160} />
                </div>
              )}
              {secret && (
                <p className="mt-3 text-xs text-zinc-500">
                  Or enter the secret manually:{" "}
                  <code className="select-all rounded bg-zinc-800 px-1.5 py-0.5 font-code text-xs text-zinc-300">
                    {secret}
                  </code>
                </p>
              )}
            </div>

            <form onSubmit={onConfirm} className="rounded-lg border border-zinc-900 bg-[#0f0f0f] p-5">
              <label htmlFor="enroll-mfa-code" className="mb-1 block text-sm font-medium text-zinc-200">
                2. Enter the 6-digit code
              </label>
              <input
                id="enroll-mfa-code"
                ref={inputRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                className="w-full rounded border border-zinc-800 bg-[#1a1a1a] px-3 py-2 font-mono text-lg tracking-[0.4em] text-zinc-200 placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#CCFF00]"
                aria-required="true"
                aria-invalid={!!error}
              />
              <button
                type="submit"
                disabled={code.length < 6 || confirming}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded bg-[#CCFF00] px-4 py-2 text-sm font-semibold text-black hover:bg-[#b8e600] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {confirming && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                {confirming ? "Verifying…" : "Enable two-factor authentication"}
              </button>
            </form>
          </div>
        ) : (
          <p className="text-sm text-red-400">{error ?? "Could not start enrollment."}</p>
        )}

        {provisioningUri && error && (
          <p className="mt-4 text-xs text-red-400" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

// Wrap protected routes so a logged-in but un-enrolled user is forced through
// MandatoryMfaEnrollment before any console content renders. No skip path.
export function RequireMfaEnrolled({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <div style={{ padding: 24 }}>Loading…</div>;
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (user.mfaSetupRequired) {
    return <MandatoryMfaEnrollment />;
  }
  return <>{children}</>;
}
